import { useEffect, useState } from 'react';
import { Star, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { getToken } from '../../lib/auth';
import { Link } from 'react-router-dom';

interface LoyaltyPointsProps {
  title?: string;
  showTier?: boolean;
  showLifetime?: boolean;
}

interface AccountSummary {
  pointsBalance: number;
  lifetimePoints: number;
  tierCode: string | null;
  programName: string | null;
  currentTierDetails: { name: string; color: string } | null;
}

export function LoyaltyPointsBlock({ props }: { props: LoyaltyPointsProps }) {
  const [account, setAccount] = useState<AccountSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const authed = !!getToken();

  useEffect(() => {
    if (!authed) {
      setLoading(false);
      return;
    }
    api
      .get<{ enrolled: boolean; accounts: AccountSummary[] }>('/customer/loyalty')
      .then((res) => {
        if (res.data.enrolled && res.data.accounts.length > 0) {
          setAccount(res.data.accounts[0]);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authed]);

  if (!authed) {
    return (
      <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-6 py-8 text-center">
        <Star size={28} className="mx-auto mb-3 text-zinc-300" />
        <p className="text-sm text-zinc-500">
          <Link to="/login" className="underline hover:no-underline text-zinc-700 font-medium">
            Log in
          </Link>{' '}
          to see your loyalty info
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 size={20} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!account) {
    return (
      <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-6 py-8 text-center">
        <Star size={28} className="mx-auto mb-3 text-zinc-300" />
        <p className="text-sm text-zinc-500">Not enrolled in a loyalty program yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-zinc-100 rounded-2xl p-6 shadow-sm space-y-4">
      {props.title && <h3 className="text-base font-semibold text-zinc-900">{props.title}</h3>}

      <div className="flex items-end gap-3">
        <div>
          <p className="text-4xl font-extrabold text-zinc-900 tracking-tight">
            {account.pointsBalance.toLocaleString()}
          </p>
          <p className="text-sm text-zinc-500 mt-0.5">points available</p>
        </div>

        {props.showTier !== false && account.currentTierDetails && (
          <span
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold mb-1"
            style={{
              backgroundColor: account.currentTierDetails.color + '20',
              color: account.currentTierDetails.color,
            }}
          >
            <Star size={12} />
            {account.currentTierDetails.name}
          </span>
        )}
      </div>

      {props.showLifetime && (
        <p className="text-xs text-zinc-400">
          {account.lifetimePoints.toLocaleString()} lifetime points earned
        </p>
      )}
    </div>
  );
}
